import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DAY_MS, addDays, startOfDay } from "../../utils/ganttModel";
import "./Gantt.css";

// Generic two-pane timeline. Callers hand it display rows; it owns the time
// scale, zoom, shading, dependency lines, tooltips and row virtualization.
//
// row: { key, height, label, className?, laneHeight?, barHeight?, lanes?, bars }
// bar: { id, start, end, lane?, kind?: "actual"|"projected"|"summary",
//        color?, label?, overdueFrom?, scheduled?, tooltip?, onClick? }
// dependency: { id, from, to, tooltip? }   (from/to are row keys)

export const LEFT_WIDTH = 300;
const HEADER_HEIGHT = 52;
const OVERSCAN_PX = 400;

export const ZOOMS = {
  day: { label: "Day", pxPerDay: 44, pad: 3, top: "month", bottom: "day" },
  week: { label: "Week", pxPerDay: 18, pad: 10, top: "month", bottom: "week" },
  month: {
    label: "Month",
    pxPerDay: 5,
    pad: 45,
    top: "year",
    bottom: "month",
  },
  quarter: {
    label: "Quarter",
    pxPerDay: 1.6,
    pad: 90,
    top: "year",
    bottom: "quarter",
  },
};

function startOfUnit(date, unit) {
  const d = startOfDay(date);
  if (unit === "week") return addDays(d, -((d.getDay() + 6) % 7)); // Monday
  if (unit === "month") return new Date(d.getFullYear(), d.getMonth(), 1);
  if (unit === "quarter")
    return new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);
  if (unit === "year") return new Date(d.getFullYear(), 0, 1);
  return d;
}

function nextUnit(date, unit) {
  if (unit === "week") return addDays(date, 7);
  if (unit === "month")
    return new Date(date.getFullYear(), date.getMonth() + 1, 1);
  if (unit === "quarter")
    return new Date(date.getFullYear(), date.getMonth() + 3, 1);
  if (unit === "year") return new Date(date.getFullYear() + 1, 0, 1);
  return addDays(date, 1);
}

function buildTicks(rangeStart, rangeEnd, unit) {
  const ticks = [];
  let cursor = startOfUnit(rangeStart, unit);
  while (cursor < rangeEnd) {
    const next = nextUnit(cursor, unit);
    ticks.push({
      start: cursor < rangeStart ? rangeStart : cursor,
      end: next < rangeEnd ? next : rangeEnd,
      date: cursor,
    });
    cursor = next;
  }
  return ticks;
}

function tickLabel(date, unit) {
  if (unit === "year") return String(date.getFullYear());
  if (unit === "quarter") return `Q${Math.floor(date.getMonth() / 3) + 1}`;
  if (unit === "month")
    return date.toLocaleDateString(undefined, { month: "short" });
  if (unit === "week")
    return date.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
    });
  return String(date.getDate());
}

function topLabel(date, unit) {
  if (unit === "year") return String(date.getFullYear());
  return date.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

export function pickDefaultZoom(min, max) {
  const spanDays = (max - min) / DAY_MS;
  if (spanDays <= 21) return "day";
  if (spanDays <= 120) return "week";
  if (spanDays <= 540) return "month";
  return "quarter";
}

// First index whose row bottom reaches `y` (rows are sorted by top).
function firstRowAt(layout, y) {
  let lo = 0;
  let hi = layout.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (layout[mid].top + layout[mid].height < y) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

export function GanttLegend({ items }) {
  return (
    <div className="gantt-legend">
      {items.map((item) => (
        <span key={item.label} className="gantt-legend-item">
          <span
            className={`gantt-legend-swatch ${item.className || ""}`}
            style={item.color ? { background: item.color } : undefined}
          />
          {item.label}
        </span>
      ))}
    </div>
  );
}

export default function GanttView({
  rows,
  dependencies = [],
  min,
  max,
  now,
  calendar,
  zoom,
  onZoomChange,
  leftHeader = "Stage",
  toolbar = null,
  legend = null,
  dimOnRowHover = false,
  maxHeight = "70vh",
  fill = false, // stretch to the parent's height instead of maxHeight
}) {
  const [hover, setHover] = useState(null); // { rowKey?, depId?, content? }
  const [viewportWidth, setViewportWidth] = useState(0);
  const [windowRows, setWindowRows] = useState({ start: 0, end: 60 });
  const scrollRef = useRef(null);
  const tooltipRef = useRef(null);
  const focusRef = useRef(null); // { date, at } to bring into view after layout
  const initialisedRef = useRef(false);
  const scrollFrameRef = useRef(null);

  const scale = ZOOMS[zoom];

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const update = () => setViewportWidth(el.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const { rangeStart, rangeEnd, width } = useMemo(() => {
    const start = startOfUnit(addDays(min, -scale.pad), scale.bottom);
    let end = nextUnit(
      startOfUnit(addDays(max, scale.pad), scale.bottom),
      scale.bottom,
    );
    // Keep the grid at least as wide as the viewport so it never ends mid-pane.
    const minDays = Math.max(viewportWidth - LEFT_WIDTH, 0) / scale.pxPerDay;
    while ((end - start) / DAY_MS < minDays) end = nextUnit(end, scale.bottom);
    return {
      rangeStart: start,
      rangeEnd: end,
      width: Math.round(((end - start) / DAY_MS) * scale.pxPerDay),
    };
  }, [min, max, scale, viewportWidth]);

  const xOf = useCallback(
    (date) => ((new Date(date) - rangeStart) / DAY_MS) * scale.pxPerDay,
    [rangeStart, scale],
  );

  const layout = useMemo(() => {
    let top = 0;
    return rows.map((row) => {
      const entry = { top, height: row.height };
      top += row.height;
      return entry;
    });
  }, [rows]);
  const bodyHeight = layout.length
    ? layout[layout.length - 1].top + layout[layout.length - 1].height
    : 0;
  const rowIndex = useMemo(
    () => new Map(rows.map((row, i) => [row.key, i])),
    [rows],
  );

  // Only rows near the viewport are mounted; the rest are empty space in a
  // body of the full height, so scrolling hundreds of rows stays cheap.
  const updateWindow = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const top = Math.max(el.scrollTop - OVERSCAN_PX, 0);
    const bottom = el.scrollTop + el.clientHeight - HEADER_HEIGHT + OVERSCAN_PX;
    const start = firstRowAt(layout, top);
    let end = start;
    while (end < layout.length && layout[end].top <= bottom) end += 1;
    setWindowRows((prev) =>
      prev.start === start && prev.end === end ? prev : { start, end },
    );
  }, [layout]);

  useLayoutEffect(() => {
    updateWindow();
  }, [updateWindow]);

  useEffect(() => () => cancelAnimationFrame(scrollFrameRef.current), []);

  const onScroll = () => {
    if (scrollFrameRef.current) return;
    scrollFrameRef.current = requestAnimationFrame(() => {
      scrollFrameRef.current = null;
      updateWindow();
    });
  };

  const timelineWidth = Math.max(viewportWidth - LEFT_WIDTH, 0);

  // Open on today (clamped to the data), and keep the centre date in place
  // when the zoom changes.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !viewportWidth) return;
    if (!initialisedRef.current) {
      initialisedRef.current = true;
      focusRef.current = {
        date: new Date(Math.min(Math.max(now, min), max)),
        at: 0.35,
      };
    }
    if (focusRef.current) {
      const { date, at } = focusRef.current;
      focusRef.current = null;
      el.scrollLeft = Math.max(xOf(date) - timelineWidth * at, 0);
    }
  });

  const changeZoom = (next) => {
    const el = scrollRef.current;
    if (el) {
      const centerX = el.scrollLeft + timelineWidth / 2;
      focusRef.current = {
        date: new Date(
          rangeStart.getTime() + (centerX / scale.pxPerDay) * DAY_MS,
        ),
        at: 0.5,
      };
    }
    onZoomChange(next);
  };

  const nonWorkingDays = useMemo(() => {
    const days = [];
    if (scale.pxPerDay < 4) return days; // too narrow to read at quarter zoom
    for (let d = startOfDay(rangeStart); d < rangeEnd; d = addDays(d, 1)) {
      const info = calendar.dayType(d);
      if (info.type === "WORKING") continue;
      // At month zoom weekends would just be noise; holidays still matter.
      if (zoom === "month" && info.type !== "HOLIDAY") continue;
      days.push({ date: d, ...info });
    }
    return days;
  }, [rangeStart, rangeEnd, calendar, zoom, scale.pxPerDay]);

  const topTicks = useMemo(
    () => buildTicks(rangeStart, rangeEnd, scale.top),
    [rangeStart, rangeEnd, scale.top],
  );
  const bottomTicks = useMemo(
    () => buildTicks(rangeStart, rangeEnd, scale.bottom),
    [rangeStart, rangeEnd, scale.bottom],
  );

  const barTop = (rowIdx, lane = 0) => {
    const row = rows[rowIdx];
    const { top, height } = layout[rowIdx];
    const laneHeight = row.laneHeight || 26;
    const barHeight = row.barHeight || 18;
    const stack = (row.lanes || 1) * laneHeight;
    return (
      top +
      (height - stack) / 2 +
      lane * laneHeight +
      (laneHeight - barHeight) / 2
    );
  };

  const dependencyPaths = useMemo(() => {
    return dependencies
      .map((dep) => {
        const fromIdx = rowIndex.get(dep.from);
        const toIdx = rowIndex.get(dep.to);
        if (fromIdx === undefined || toIdx === undefined) return null;
        // Skip lines whose whole span is off-screen.
        if (
          Math.max(fromIdx, toIdx) < windowRows.start ||
          Math.min(fromIdx, toIdx) >= windowRows.end
        )
          return null;
        const fromRow = rows[fromIdx];
        const toRow = rows[toIdx];
        if (!fromRow.bars.length || !toRow.bars.length) return null;
        const fromBar = fromRow.bars.reduce((a, b) => (b.end > a.end ? b : a));
        const toBar = toRow.bars.reduce((a, b) => (b.start < a.start ? b : a));
        const x1 = xOf(fromBar.end);
        const y1 =
          barTop(fromIdx, fromBar.lane) + (fromRow.barHeight || 18) / 2;
        const x2 = xOf(toBar.start);
        const y2 = barTop(toIdx, toBar.lane) + (toRow.barHeight || 18) / 2;
        const stub = 10;
        let d;
        if (x2 - stub >= x1 + stub) {
          d = `M ${x1} ${y1} H ${x1 + stub} V ${y2} H ${x2}`;
        } else {
          // Successor starts before the predecessor ends (overlap or rework):
          // route around through the gap above the target row.
          const gapY = layout[toIdx].top + 3;
          d = `M ${x1} ${y1} H ${x1 + stub} V ${gapY} H ${x2 - stub} V ${y2} H ${x2}`;
        }
        return { ...dep, d };
      })
      .filter(Boolean);
    // barTop reads rows/layout, which are already listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependencies, rowIndex, rows, layout, xOf, windowRows]);

  const moveTooltip = (event) => {
    const tip = tooltipRef.current;
    if (!tip) return;
    const margin = 14;
    const { offsetWidth: w, offsetHeight: h } = tip;
    let x = event.clientX + margin;
    let y = event.clientY + margin;
    if (x + w > window.innerWidth - 8) x = event.clientX - w - margin;
    if (y + h > window.innerHeight - 8) y = event.clientY - h - margin;
    tip.style.transform = `translate(${Math.max(x, 8)}px, ${Math.max(y, 8)}px)`;
  };

  const hoverProps = (next) => ({
    onMouseEnter: (event) => {
      setHover(next);
      const { clientX, clientY } = event;
      // Position after React paints the new content so the size is right.
      requestAnimationFrame(() => moveTooltip({ clientX, clientY }));
    },
    onMouseMove: moveTooltip,
    onMouseLeave: () => setHover(null),
  });

  const todayX = xOf(now);
  const hoveredRow = hover?.rowKey || null;
  const hoveredDep = hover?.depId
    ? dependencies.find((d) => d.id === hover.depId)
    : null;
  const isDepEnd = (key) =>
    !!hoveredDep && (hoveredDep.from === key || hoveredDep.to === key);
  const rowHighlighted = (key) => hoveredRow === key || isDepEnd(key);

  const visibleIndexes = [];
  for (
    let i = windowRows.start;
    i < Math.min(windowRows.end, rows.length);
    i += 1
  )
    visibleIndexes.push(i);

  return (
    <div className={`panel gantt ${fill ? "gantt-fill" : ""}`}>
      <div className="gantt-toolbar">
        {toolbar}
        <div className="gantt-zoom" role="group" aria-label="Timeline scale">
          {Object.entries(ZOOMS).map(([key, value]) => (
            <button
              key={key}
              type="button"
              className={key === zoom ? "active" : ""}
              onClick={() => changeZoom(key)}
            >
              {value.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="gantt-today-btn"
          onClick={() => {
            const el = scrollRef.current;
            if (el) el.scrollLeft = Math.max(todayX - timelineWidth * 0.35, 0);
          }}
        >
          Today
        </button>
        {legend && <GanttLegend items={legend} />}
      </div>

      <div
        className="gantt-scroll"
        ref={scrollRef}
        style={fill ? undefined : { maxHeight }}
        onScroll={onScroll}
      >
        <div className="gantt-inner" style={{ width: LEFT_WIDTH + width }}>
          <div className="gantt-header">
            <div className="gantt-corner" style={{ width: LEFT_WIDTH }}>
              {leftHeader}
            </div>
            <div className="gantt-scale" style={{ width }}>
              <div className="gantt-scale-top">
                {topTicks.map((tick) => (
                  <div
                    key={tick.date.getTime()}
                    className="gantt-tick"
                    style={{
                      left: xOf(tick.start),
                      width: xOf(tick.end) - xOf(tick.start),
                    }}
                  >
                    <span>{topLabel(tick.date, scale.top)}</span>
                  </div>
                ))}
              </div>
              <div className="gantt-scale-bottom">
                {bottomTicks.map((tick) => {
                  const info =
                    scale.bottom === "day" ? calendar.dayType(tick.date) : null;
                  return (
                    <div
                      key={tick.date.getTime()}
                      className={`gantt-tick ${info && info.type !== "WORKING" ? "is-off" : ""}`}
                      style={{
                        left: xOf(tick.start),
                        width: xOf(tick.end) - xOf(tick.start),
                      }}
                    >
                      <span>{tickLabel(tick.date, scale.bottom)}</span>
                      {scale.bottom === "day" && (
                        <span className="gantt-tick-sub">
                          {tick.date.toLocaleDateString(undefined, {
                            weekday: "narrow",
                          })}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
              {todayX >= 0 && todayX <= width && (
                <div className="gantt-today-flag" style={{ left: todayX }}>
                  Today
                </div>
              )}
            </div>
          </div>

          <div className="gantt-body" style={{ height: bodyHeight }}>
            <div
              className="gantt-left"
              style={{ width: LEFT_WIDTH, height: bodyHeight }}
            >
              {visibleIndexes.map((i) => {
                const row = rows[i];
                return (
                  <div
                    key={row.key}
                    className={`gantt-left-row ${row.className || ""} ${rowHighlighted(row.key) ? "is-hovered" : ""}`}
                    style={{ top: layout[i].top, height: layout[i].height }}
                    onMouseEnter={() => setHover({ rowKey: row.key })}
                    onMouseLeave={() => setHover(null)}
                  >
                    {row.label}
                  </div>
                );
              })}
            </div>

            <div className="gantt-grid" style={{ width, height: bodyHeight }}>
              {visibleIndexes.map((i) => (
                <div
                  key={rows[i].key}
                  className={`gantt-row-bg ${rows[i].className || ""} ${hoveredRow === rows[i].key ? "is-hovered" : ""}`}
                  style={{ top: layout[i].top, height: layout[i].height }}
                />
              ))}
              {bottomTicks.map((tick) => (
                <div
                  key={`line-${tick.date.getTime()}`}
                  className="gantt-gridline"
                  style={{ left: xOf(tick.start) }}
                />
              ))}
              {nonWorkingDays.map((day) => (
                <div
                  key={day.date.getTime()}
                  className={`gantt-off ${day.type === "HOLIDAY" ? "is-holiday" : ""}`}
                  style={{
                    left: xOf(day.date),
                    width: Math.max(scale.pxPerDay, 2),
                  }}
                  {...hoverProps({
                    content: (
                      <>
                        <div className="gantt-tip-title">
                          {day.type === "HOLIDAY" ? day.label : "Weekly off"}
                        </div>
                        <div className="gantt-tip-note">
                          {day.date.toLocaleDateString(undefined, {
                            weekday: "long",
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })}
                          {day.type === "HOLIDAY" ? " · Public holiday" : ""}
                        </div>
                      </>
                    ),
                  })}
                />
              ))}
              {todayX >= 0 && todayX <= width && (
                <div className="gantt-today-line" style={{ left: todayX }} />
              )}

              <svg className="gantt-deps" width={width} height={bodyHeight}>
                <defs>
                  <marker
                    id="gantt-arrow"
                    viewBox="0 0 8 8"
                    refX="7"
                    refY="4"
                    markerWidth="7"
                    markerHeight="7"
                    orient="auto-start-reverse"
                  >
                    <path d="M0,0 L8,4 L0,8 z" fill="#64748b" />
                  </marker>
                  <marker
                    id="gantt-arrow-active"
                    viewBox="0 0 8 8"
                    refX="7"
                    refY="4"
                    markerWidth="7"
                    markerHeight="7"
                    orient="auto-start-reverse"
                  >
                    <path d="M0,0 L8,4 L0,8 z" fill="#1d4ed8" />
                  </marker>
                </defs>
                {dependencyPaths.map((dep) => {
                  const active =
                    hoveredDep?.id === dep.id ||
                    hoveredRow === dep.from ||
                    hoveredRow === dep.to;
                  return (
                    <g key={dep.id}>
                      <path
                        d={dep.d}
                        className={`gantt-dep ${active ? "is-active" : ""}`}
                        markerEnd={`url(#${active ? "gantt-arrow-active" : "gantt-arrow"})`}
                      />
                      <path
                        d={dep.d}
                        className="gantt-dep-hit"
                        {...hoverProps({ depId: dep.id, content: dep.tooltip })}
                      />
                    </g>
                  );
                })}
              </svg>

              {visibleIndexes.map((i) => {
                const row = rows[i];
                const dimmed =
                  (dimOnRowHover && hoveredRow && hoveredRow !== row.key) ||
                  (hoveredDep && !isDepEnd(row.key));
                const barHeight = row.barHeight || 18;
                return row.bars.map((bar) => {
                  const left = xOf(bar.start);
                  const barWidth = Math.max(xOf(bar.end) - left, 4);
                  const overdueStart = bar.overdueFrom
                    ? xOf(bar.overdueFrom) - left
                    : null;
                  return (
                    <div
                      key={bar.id}
                      className={`gantt-bar gantt-bar-${bar.kind || "actual"} ${bar.scheduled ? "is-scheduled" : ""} ${dimmed ? "is-dimmed" : ""}`}
                      style={{
                        left,
                        width: barWidth,
                        top: barTop(i, bar.lane),
                        height: barHeight,
                        background:
                          bar.kind === "projected" ? undefined : bar.color,
                      }}
                      onClick={bar.onClick}
                      {...hoverProps({ rowKey: row.key, content: bar.tooltip })}
                    >
                      {overdueStart !== null && overdueStart < barWidth && (
                        <span
                          className="gantt-bar-overdue"
                          style={{ left: Math.max(overdueStart, 0) }}
                        />
                      )}
                      {bar.label && barWidth > 56 && (
                        <span className="gantt-bar-label">{bar.label}</span>
                      )}
                    </div>
                  );
                });
              })}
            </div>
          </div>
        </div>
      </div>

      <div
        ref={tooltipRef}
        className={`gantt-tooltip ${hover?.content ? "is-visible" : ""}`}
        role="tooltip"
      >
        {hover?.content}
      </div>
    </div>
  );
}
